import React from 'react';

// API/Components
import { buildMeasurementText, AnalysisResultToStateColor, ANALYSIS_RESULT, STATE_COLOR } from './enums';

// Material UI
import { Typography, Tooltip } from '@mui/material';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';


// Build a data grid measurement cell with color coding based on analysis result
export function BuildDataGridMeasurementCell(measurement, stateType, roundingOverride = null, diffRoundingOverride = null, blankValueOverride = null) {

	// Extract measurement details
	const measValue = measurement.measurement;
	const isMeasValid = measurement.valid;
	const diffValue = measurement.out;
	const analysisResult = measurement.result;

	// Build the measurement text
	const { measText, outText } = buildMeasurementText(measValue, isMeasValid, diffValue, analysisResult, stateType, roundingOverride, diffRoundingOverride, blankValueOverride);
	const cellText = `${measText} ${outText}`;

	// Determine the cell color from the analysis result
	const statusColor = AnalysisResultToStateColor(analysisResult) + 'aa'; // Add transparency to the color

	return BuildDataGridCell(cellText, 'center', statusColor);
}

// Build a generic measurement cell with specified units and formatting
export function BuildGenericMeasurementCell(value, units, rounding = 3, blankValue = '--.-') {
	
	var valueText = blankValue;
	if (value !== undefined && value !== null && !isNaN(value) && !isNaN(rounding) && rounding >= 0) {
		valueText = value.toFixed(rounding);
	}

	const cellText = `${valueText} ${units}`;

	return BuildDataGridCell(cellText);
}

// Build a discharge event cell with the specified event type code
export function BuildDischargeEventCell(eventTypeCode) {
	
	var eventType = 'Unknown';
	var eventIcon = <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN, mr: 1 }}/>;
	
	switch (eventTypeCode) {
		case 0:
			eventType =  'Unknown';
			eventIcon = <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN, mr: 1 }}/>;
			break;
			
		case 1:
			eventType =  'Discharge';
			eventIcon = <ArrowCircleDownOutlinedIcon sx={{ color: STATE_COLOR.CRITICAL, mr: 1 }}/>
			break;

		case 2:
			eventType =  'Charge';
			eventIcon = <ArrowCircleUpOutlinedIcon sx={{ color: STATE_COLOR.WITHIN_LIMITS, mr: 1 }}/>
			break;

			default:
			eventType =  'Unknown';
			eventIcon = <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN, mr: 1 }}/>;
			break;
	}

	return BuildDataGridCell(eventType, 'center', null, true, eventIcon);
}

// Build a data grid cell with the specified text, alignment, and optional colorization
export function BuildDataGridCell(text, alignment = 'center', backgroundColor = null, useDefaultFontFamily = true, iconComponent = null) {
    const style = {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: alignment,
    };

    if (backgroundColor) {
        style.backgroundColor = backgroundColor;
    }

    return (
        <div style={style}>
            {iconComponent}
            <Typography
                {...(useDefaultFontFamily && { fontFamily: 'Saira, sans-serif' })}
            >
                {text}
            </Typography>
        </div>
    );
}

// Get a colorized analysis icon wrapped in a tooltip based on the analysis result status
export function GetAnalysisIcon(status) {

	let icon = <HelpOutlineOutlinedIcon style={{ color: 'black' }} />;
	let tooltip = "Unknown Status";

	if (
		status === ANALYSIS_RESULT.ANALYSIS_TEMP_SENSOR_ABOVE_DIFF_LIMIT ||
		status === ANALYSIS_RESULT.ANALYSIS_OUT_OF_RANGE_CRITICAL ||
		status === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL ||
		status === ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL
	) {
		icon = <ErrorOutlineIcon style={{ color: 'black' }} />;
		tooltip = "Critical Analysis";
	} else if (
		status === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS ||
		status === ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS
	) {
		icon = <WarningAmberOutlinedIcon style={{ color: STATE_COLOR.MAINTENANCE }} />;
		tooltip = "Maintenance Analysis";
	} else if (
		status === ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS
	) {
		icon = <CheckCircleOutlineOutlinedIcon style={{ color: STATE_COLOR.WITHIN_LIMITS }} />;
		tooltip = "Within Limits";
	} else {
		icon = <HelpOutlineOutlinedIcon style={{ color: 'black' }} />;
		tooltip = "Unknown Status";
	}

	return <Tooltip title={tooltip} placement="top">{icon}</Tooltip>;
}